import { css } from '@emotion/react';
import {
  type FC, useEffect, useMemo, useState,
} from 'react';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import { type DropdownOption } from '~/_shared/baseComponents/dropdown';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { type SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '~/_shared/utils/hooks';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { DEFAULT_RANGES_COUNT } from '~/boundary/settings/useBoundaryFillSettingsPropsVariants';
import { useIsBoundaryToolReady } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/useIsBoundaryToolReady';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  BoundaryTerritoryType, type CalculateBucketFunctionNumeric,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { ActiveBoundaryFillEditComponent } from '../../activeBoundary/edit/activeBoundaryFillEditComponent';
import { useActiveBoundaryFillEdit } from '../../activeBoundary/edit/useActiveBoundaryFillEdit';
import { BoundarySelectedOption } from '../../fill/boundaryFill.enums';
import { LoadBoundaryPaneSubmitButtonComponent } from '../loadBoundaryPaneSubmitButton/boundaryCreateSubmitButton.component';

export type LoadGlobalBoundaryGroupPaneComponentProps = Readonly<{
  globalBoundaryGroups: ReadonlyArray<BoundaryGroup>;
  initialBoundaryGroupId?: BoundaryGroupId | null;
  initialBoundaryTerritoryType?: BoundaryTerritoryType | null;
  initialDemographicId?: string | null;
  onAddBoundary: (data: LoadGlobalBoundaryGroupData) => void;
}>;

const panelDropdownSectionStyle = css({
  marginBottom: 8,
});

const labelStyle = css({
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '4px',
  textTransform: 'uppercase',
});

export type LoadGlobalBoundaryGroupData = Readonly<{
  boundaryGroupId: number;
  boundaryTerritoryType: BoundaryTerritoryType;
  boundaryFillData: SpreadsheetColumnId | null;
  demographicId: string | null;
  numericCalculateBucketFunction: CalculateBucketFunctionNumeric | null;
}>;

export const LoadGlobalBoundaryGroupPaneComponent: FC<LoadGlobalBoundaryGroupPaneComponentProps> = ({
  onAddBoundary,
  globalBoundaryGroups,
  initialBoundaryGroupId = null,
  initialBoundaryTerritoryType = BoundaryTerritoryType.Manual,
  initialDemographicId = null,
}) => {
  const [boundaryGroupId, setBoundaryGroupId] = useState<BoundaryGroupId | null>(initialBoundaryGroupId);
  const { isBoundaryToolPreparing, boundaryToolDisabledReason } = useIsBoundaryToolReady();

  useEffect(() => {
    setBoundaryGroupId(initialBoundaryGroupId);
  }, [initialBoundaryGroupId]);

  const {
    isUpdateDisabled, boundaryFillModel, ...boundaryFillProps
  } = useActiveBoundaryFillEdit({
    boundaryGroupId,
    fixedRangeCount: DEFAULT_RANGES_COUNT,
    initialBoundaryTerritoryType,
    initialDemographicId,
  });

  const boundaryTerritoryType = boundaryFillModel.boundaryTerritoryType;

  const [t] = useTranslation();

  const globalBoundaryGroupsListOptions: DropdownOption<number>[] = useMemo(() =>
    globalBoundaryGroups.map(item => ({
      name: item.name,
      value: item.id,
    })), [globalBoundaryGroups]);

  const isAddDisabled =
    (boundaryGroupId === null) ||
    (boundaryTerritoryType === null) ||
    isUpdateDisabled ||
    isBoundaryToolPreparing;

  return (
    <div>
      <div css={panelDropdownSectionStyle}>
        <div css={labelStyle}>2. {t(BoundarySelectedOption.SelectBoundaryType)}</div>
        <AutocompleteComponent
          onChange={setBoundaryGroupId}
          options={globalBoundaryGroupsListOptions}
          placeholder={t('Type to search...')}
          value={boundaryGroupId}
          isClearable
        />
      </div>

      {boundaryGroupId !== null && (
        <ActiveBoundaryFillEditComponent
          labelCountStart={3}
          {...boundaryFillProps}
          {...boundaryFillModel}
        />
      )}

      <TooltipComponent
        tooltipContent={boundaryToolDisabledReason}
        placement={TooltipPlacement.Bottom}
      >
        <LoadBoundaryPaneSubmitButtonComponent
          text={t('Load Boundaries')}
          onClick={isAddDisabled ? undefined : () => onAddBoundary({
            boundaryGroupId,
            boundaryTerritoryType,
            boundaryFillData: boundaryFillModel.boundaryFillData,
            demographicId: boundaryFillModel.selectedDemographicId,
            numericCalculateBucketFunction: boundaryFillModel.numericCalculateBucketFunction,
          })}
          isDisabled={isAddDisabled}
        />
      </TooltipComponent>
    </div>
  );
};
